import React from "react";

interface LegalSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
  level?: number;
}

export const LegalSection: React.FC<LegalSectionProps> = ({
  id,
  title,
  children,
  level = 1,
}) => {
  const HeadingTag =
    `h${Math.min(level + 1, 6)}` as keyof React.JSX.IntrinsicElements;

  const headingClasses = {
    1: "text-2xl font-bold text-gray-900 mb-8 mt-12 first:mt-0 relative",
    2: "text-xl font-semibold text-gray-800 mb-6 mt-8 relative",
    3: "text-lg font-medium text-gray-800 mb-4 mt-6 relative",
    4: "text-base font-medium text-gray-800 mb-3 mt-5 relative",
  };

  const getSectionIcon = (level: number) => {
    switch (level) {
      case 1:
        return (
          <div className="absolute -left-2 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full"></div>
        );
      case 2:
        return (
          <div className="absolute -left-1 top-1/2 transform -translate-y-1/2 w-0.5 h-6 bg-blue-400 rounded-full"></div>
        );
      default:
        return null;
    }
  };

  return (
    <section id={id} className="scroll-mt-32 relative">
      <HeadingTag
        className={
          headingClasses[level as keyof typeof headingClasses] ||
          headingClasses[4]
        }
      >
        {getSectionIcon(level)}
        <span className="relative z-10">{title}</span>
      </HeadingTag>
      <div className="prose prose-gray max-w-none mb-8 leading-relaxed">
        <div className="space-y-6">
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              const childProps = child.props as { className?: string };

              // Enhanced styling for different elements
              if (child.type === "p") {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} text-gray-700 leading-7 mb-4`,
                } as Partial<unknown>);
              }
              if (child.type === "ul" || child.type === "ol") {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} space-y-3 mb-6`,
                } as Partial<unknown>);
              }
              if (child.type === "li") {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} text-gray-700 leading-6 flex items-start`,
                } as Partial<unknown>);
              }
              if (child.type === "strong") {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} font-semibold text-gray-900`,
                } as Partial<unknown>);
              }
              if (child.type === "a") {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200 underline decoration-2 underline-offset-2`,
                } as Partial<unknown>);
              }
              if (child.type === "h3") {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} text-lg font-semibold text-gray-800 mb-4 mt-6 flex items-center`,
                } as Partial<unknown>);
              }
              if (child.type === "h4") {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} text-base font-medium text-gray-800 mb-3 mt-5`,
                } as Partial<unknown>);
              }
              if (
                child.type === "div" &&
                childProps.className?.includes("bg-gray-50")
              ) {
                return React.cloneElement(child, {
                  className: `${childProps.className || ""} bg-gradient-to-r from-gray-50/80 to-blue-50/30 backdrop-blur-sm border border-gray-200/50 rounded-lg shadow-sm`,
                } as Partial<unknown>);
              }
            }
            return child;
          })}
        </div>
      </div>
    </section>
  );
};
